import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getFirebaseToken } from "../utils/getFirebaseToken";
import {
  approvePurchaseOrder,
  createPurchaseOrder,
  fetchPurchaseOrderById,
  receivePurchaseOrder,
  updatePurchaseOrder,
} from "../services/erpPurchaseOrders";
import {
  fetchLocations,
  fetchPartners,
  fetchResourceItems,
  fetchUom,
  fetchWarehouses,
} from "../services/erpMasterData";
import {
  fetchSourcingByItem,
  fetchSupplierItems,
} from "../services/erpSupplierItems";

function todayDate() {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 10);
}

function emptyHeader() {
  return {
    partner_id: "",
    order_date: todayDate(),
    expected_date: "",
    currency: "RSD",
    remarks: "",
    status: "DRAFT",
  };
}

function createEmptyLine(nextNo = 1) {
  return {
    id: null,
    line_no: nextNo,
    item_id: "",
    supplier_item_id: "",
    supplier_sku: "",
    supplier_name: "",
    description: "",
    qty: 1,
    received_qty: 0,
    uom_id: "",
    unit_price: 0,
    discount_percent: 0,
    net_unit_price: 0,
    line_total: 0,
    currency: "RSD",
    expected_date: "",
    remarks: "",
  };
}

export default function PurchaseOrderEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [header, setHeader] = useState(emptyHeader());
  const [lines, setLines] = useState([]);

  const [partners, setPartners] = useState([]);
  const [items, setItems] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [supplierItems, setSupplierItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);

  const [loading, setLoading] = useState(false);
  const [masterLoading, setMasterLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [receiptWarehouseId, setReceiptWarehouseId] = useState("");
  const [receiptLocationId, setReceiptLocationId] = useState("");
  const [receiptPostingDate, setReceiptPostingDate] = useState(todayDate());
  const [receiptRemarks, setReceiptRemarks] = useState("");
  const [receiptQtyByLine, setReceiptQtyByLine] = useState({});

  const isLocked = ["RECEIVED", "CLOSED", "CANCELLED"].includes(header.status);

  const itemMap = useMemo(() => {
    const map = {};
    items.forEach((x) => {
      map[x.id] = x;
    });
    return map;
  }, [items]);

  const supplierItemMap = useMemo(() => {
    const map = {};
    supplierItems.forEach((x) => {
      map[x.id] = x;
    });
    return map;
  }, [supplierItems]);

  const totalAmount = useMemo(() => {
    return lines.reduce((sum, line) => sum + Number(line.line_total || 0), 0);
  }, [lines]);

  const canApprove =
    !isNew &&
    header.status === "DRAFT" &&
    Array.isArray(lines) &&
    lines.length > 0 &&
    header.partner_id;

  const loadMasterData = async () => {
    setMasterLoading(true);
    try {
      const token = await getFirebaseToken();

      const [
        partnersRes,
        itemsRes,
        uomRes,
        supplierItemsRes,
        warehousesRes,
        locationsRes,
      ] = await Promise.all([
        fetchPartners(token),
        fetchResourceItems(token),
        fetchUom(token),
        fetchSupplierItems(token, { is_active: 1 }),
        fetchWarehouses(token),
        fetchLocations(token),
      ]);

      setPartners(partnersRes.rows || []);
      setItems(itemsRes.rows || []);
      setUoms(uomRes.rows || []);
      setSupplierItems(supplierItemsRes.rows || []);
      setWarehouses(warehousesRes.rows || []);
      setLocations(locationsRes.rows || []);
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.message || e.message || "Failed to load master data."
      );
    } finally {
      setMasterLoading(false);
    }
  };

  const loadDocument = async () => {
    if (isNew) return;

    setLoading(true);
    try {
      const token = await getFirebaseToken();
      const res = await fetchPurchaseOrderById(token, id);
      const data = res.data || res;

      setHeader({
        ...data.header,
        partner_id: data.header?.partner_id || "",
        order_date: String(data.header?.order_date || "").slice(0, 10),
        expected_date: String(data.header?.expected_date || "").slice(0, 10),
      });

      setLines(
        (data.lines || []).map((line, idx) => ({
          ...line,
          line_no: line.line_no || idx + 1,
        }))
      );
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.message || e.message || "Failed to load purchase order."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMasterData();
    loadDocument();
  }, [id]);

  const recalcLine = (line) => {
    const unitPrice = Number(line.unit_price || 0);
    const discountPercent = Number(line.discount_percent || 0);
    const qty = Number(line.qty || 0);
    const netUnitPrice = unitPrice * (1 - discountPercent / 100);
    const lineTotal = qty * netUnitPrice;

    return {
      ...line,
      net_unit_price: netUnitPrice,
      line_total: lineTotal,
    };
  };

  const getRemainingQty = (line) => {
    return Number(line.qty || 0) - Number(line.received_qty || 0);
  };

  const setLineField = async (index, field, value) => {
    const next = [...lines];
    let line = {
      ...next[index],
      [field]: value,
    };

    if (field === "item_id") {
      const item = itemMap[value];

      if (item?.uom_id) {
        line.uom_id = item.uom_id;
      }

      if (!line.description && item?.name) {
        line.description = item.name;
      }

      line.supplier_item_id = "";
      line.supplier_sku = "";
      line.supplier_name = "";
      line.unit_price = 0;
      line.discount_percent = 0;
      line.net_unit_price = 0;
      line.line_total = 0;
      line.currency = header.currency || "RSD";

      try {
        const token = await getFirebaseToken();
        const sourcingRes = await fetchSourcingByItem(token, value);
        const sourcing = (sourcingRes.rows || []).filter((x) => {
          return (
            !header.partner_id ||
            Number(x.partner_id) === Number(header.partner_id)
          );
        });

        const preferred =
          sourcing.find((x) => Number(x.is_preferred) === 1) || sourcing[0];

        if (preferred) {
          line.supplier_item_id = preferred.id;
          line.supplier_sku = preferred.supplier_sku || "";
          line.supplier_name = preferred.supplier_name || "";
          line.unit_price = Number(preferred.current_price || 0);
          line.discount_percent = Number(
            preferred.current_discount_percent || 0
          );
          line.currency = preferred.current_currency || header.currency || "RSD";
        }
      } catch (e) {
        console.error("Failed to fetch sourcing:", e);
      }

      line = recalcLine(line);
    }

    if (field === "supplier_item_id") {
      const supplier = supplierItemMap[value];
      if (supplier) {
        line.supplier_sku = supplier.supplier_sku || "";
        line.supplier_name = supplier.supplier_name || "";
        line.unit_price = Number(supplier.current_price || 0);
        line.discount_percent = Number(
          supplier.current_discount_percent || 0
        );
        line.currency = supplier.current_currency || header.currency || "RSD";
      }
      line = recalcLine(line);
    }

    if (
      field === "qty" ||
      field === "unit_price" ||
      field === "discount_percent"
    ) {
      line = recalcLine(line);
    }

    next[index] = line;
    setLines(next);
  };

  const addLine = () => {
    setLines([...lines, createEmptyLine(lines.length + 1)]);
  };

  const removeLine = (index) => {
    const next = lines
      .filter((_, i) => i !== index)
      .map((line, idx) => ({
        ...line,
        line_no: idx + 1,
      }));
    setLines(next);
  };

  const save = async () => {
    setSaving(true);
    setError("");

    try {
      const token = await getFirebaseToken();

      if (!header.partner_id) {
        throw new Error("Supplier is required.");
      }

      if (!lines.length) {
        throw new Error("At least one PO line is required.");
      }

      for (const line of lines) {
        if (!line.item_id) {
          throw new Error("Each line must have an internal item.");
        }
        if (Number(line.qty || 0) <= 0) {
          throw new Error("Each line qty must be greater than 0.");
        }
      }

      const payload = {
        ...header,
        partner_id: header.partner_id ? Number(header.partner_id) : null,
        lines: lines.map((line, idx) => ({
          ...line,
          line_no: idx + 1,
          item_id: line.item_id ? Number(line.item_id) : null,
          supplier_item_id: line.supplier_item_id
            ? Number(line.supplier_item_id)
            : null,
          uom_id: line.uom_id ? Number(line.uom_id) : null,
          qty: Number(line.qty || 0),
          unit_price: Number(line.unit_price || 0),
          discount_percent: Number(line.discount_percent || 0),
        })),
      };

      let result;
      if (isNew) {
        result = await createPurchaseOrder(token, payload);
      } else {
        result = await updatePurchaseOrder(token, id, payload);
      }

      const savedId = result?.id || result?.data?.id || id;

      if (isNew && savedId) {
        navigate(`/erp/purchase-orders/${savedId}`);
      } else {
        await loadDocument();
      }
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    try {
      const token = await getFirebaseToken();
      await approvePurchaseOrder(token, id);
      await loadDocument();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e.message || "Failed to approve.");
    }
  };

  const handleReceive = async () => {
    try {
      const token = await getFirebaseToken();

      if (!receiptWarehouseId) {
        throw new Error("Receipt warehouse is required.");
      }

      if (!receiptLocationId) {
        throw new Error("Receipt location is required.");
      }

      const receiptLines = lines
        .map((line) => ({
          po_line_id: line.id,
          receive_qty: Number(receiptQtyByLine[line.id] || 0),
        }))
        .filter((x) => x.receive_qty > 0);

      if (!receiptLines.length) {
        throw new Error("Enter at least one receipt qty.");
      }

      await receivePurchaseOrder(token, id, {
        warehouse_id: Number(receiptWarehouseId),
        location_id: Number(receiptLocationId),
        posting_date: receiptPostingDate,
        remarks: receiptRemarks,
        lines: receiptLines,
      });

      setReceiptQtyByLine({});
      setReceiptRemarks("");
      await loadDocument();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e.message || "Failed to receive.");
    }
  };

  const filteredSupplierItemsForLine = (itemId) => {
    if (!itemId) return [];

    return supplierItems.filter((x) => {
      const sameItem = Number(x.item_id) === Number(itemId);
      const samePartner =
        !header.partner_id || Number(x.partner_id) === Number(header.partner_id);

      return sameItem && samePartner;
    });
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">
            {isNew
              ? "New Purchase Order"
              : `Purchase Order ${header.po_number || id}`}
          </h1>
          <p className="text-slate-600">
            Status: <span className="font-medium">{header.status || "DRAFT"}</span>
          </p>
        </div>

        <div className="flex gap-2">
  <button
    type="button"
    onClick={save}
    disabled={saving || isLocked}
    className="px-4 py-2 rounded-lg bg-black text-white disabled:opacity-50"
  >
    {saving ? "Saving..." : "Save"}
  </button>

  {!isNew && header.status === "DRAFT" ? (
    <button
      type="button"
      onClick={approve}
      disabled={!canApprove || isLocked}
      className="px-4 py-2 rounded-lg border disabled:opacity-50"
    >
      Approve
    </button>
  ) : null}
</div>
      </div>

      {header.partner_id ? (
        <div className="bg-blue-50 border rounded-lg px-4 py-3 text-sm text-slate-700">
          Supplier selected. PO lines will show only mapped supplier items for this
          supplier.
        </div>
      ) : (
        <div className="bg-amber-50 border rounded-lg px-4 py-3 text-sm text-slate-700">
          Select a supplier first to narrow available supplier items in PO lines.
        </div>
      )}

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3">
          {error}
        </div>
      ) : null}

      {loading || masterLoading ? (
        <div className="bg-white rounded-xl shadow-sm border p-6">Loading...</div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="grid gap-3 md:grid-cols-4">
              <select
                value={header.partner_id || ""}
                onChange={(e) => {
                  const nextPartnerId = e.target.value;

                  setHeader({ ...header, partner_id: nextPartnerId });

                  setLines((prev) =>
                    prev.map((line) =>
                      recalcLine({
                        ...line,
                        supplier_item_id: "",
                        supplier_sku: "",
                        supplier_name: "",
                        unit_price: 0,
                        discount_percent: 0,
                        net_unit_price: 0,
                        line_total: 0,
                      })
                    )
                  );
                }}
                className="border rounded-lg px-3 py-2"
              >
                <option value="">Select supplier</option>
                {partners.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code ? `${p.code} - ` : ""}
                    {p.name}
                  </option>
                ))}
              </select>

              <input
                type="date"
                value={header.order_date || ""}
                onChange={(e) =>
                  setHeader({ ...header, order_date: e.target.value })
                }
                className="border rounded-lg px-3 py-2"
              />

              <input
                type="date"
                value={header.expected_date || ""}
                onChange={(e) =>
                  setHeader({ ...header, expected_date: e.target.value })
                }
                className="border rounded-lg px-3 py-2"
              />

              <input
                type="text"
                value={header.currency || "RSD"}
                onChange={(e) =>
                  setHeader({ ...header, currency: e.target.value })
                }
                className="border rounded-lg px-3 py-2"
              />
            </div>

            <div className="mt-3">
              <textarea
                value={header.remarks || ""}
                onChange={(e) =>
                  setHeader({ ...header, remarks: e.target.value })
                }
                placeholder="Remarks"
                className="w-full border rounded-lg px-3 py-2"
                rows={3}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-4">
  <div className="flex items-center justify-between mb-4">
    <h3 className="text-lg font-semibold">PO Lines</h3>

    {!isLocked ? (
      <button
        type="button"
        onClick={addLine}
        className="px-4 py-2 rounded-lg bg-black text-white"
      >
        Add Line
      </button>
    ) : null}
  </div>

            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-2 py-2 text-left">#</th>
                    <th className="px-2 py-2 text-left min-w-[220px]">Item</th>
                    <th className="px-2 py-2 text-left min-w-[220px]">Supplier Item</th>
                    <th className="px-2 py-2 text-right">Qty</th>
                    <th className="px-2 py-2 text-left">UOM</th>
                    <th className="px-2 py-2 text-right">Unit Price</th>
                    <th className="px-2 py-2 text-right">Discount %</th>
                    <th className="px-2 py-2 text-right">Net Unit</th>
                    <th className="px-2 py-2 text-right">Line Total</th>
                    <th className="px-2 py-2 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-6 text-center text-slate-500">
                        No lines yet.
                      </td>
                    </tr>
                  ) : (
                    lines.map((line, index) => (
                      <tr key={line.id || index} className="border-t">
                        <td className="px-2 py-2">{line.line_no || index + 1}</td>

                        <td className="px-2 py-2">
                          <select
                            value={line.item_id || ""}
                            onChange={(e) =>
                              setLineField(
                                index,
                                "item_id",
                                e.target.value ? Number(e.target.value) : ""
                              )
                            }
                            className="w-full border rounded-lg px-2 py-2"
                          >
                            <option value="">Select item</option>
                            {items.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.sku} - {item.name}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td className="px-2 py-2">
                          <select
                            value={line.supplier_item_id || ""}
                            onChange={(e) =>
                              setLineField(
                                index,
                                "supplier_item_id",
                                e.target.value ? Number(e.target.value) : ""
                              )
                            }
                            className="w-full border rounded-lg px-2 py-2"
                          >
                            <option value="">Select supplier item</option>
                            {filteredSupplierItemsForLine(line.item_id).map((si) => (
                              <option key={si.id} value={si.id}>
                                {si.is_preferred ? "★ " : ""}
                                {si.source_code ? `${si.source_code} | ` : ""}
                                {si.supplier_sku} - {si.supplier_name || si.partner_name}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td className="px-2 py-2">
                          <input
                            type="number"
                            step="0.0001"
                            value={line.qty || ""}
                            onChange={(e) =>
                              setLineField(index, "qty", e.target.value)
                            }
                            className="w-full border rounded-lg px-2 py-2 text-right"
                          />
                        </td>

                        <td className="px-2 py-2">
                          <select
                            value={line.uom_id || ""}
                            onChange={(e) =>
                              setLineField(
                                index,
                                "uom_id",
                                e.target.value ? Number(e.target.value) : ""
                              )
                            }
                            className="w-full border rounded-lg px-2 py-2"
                          >
                            <option value="">Select UOM</option>
                            {uoms.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.code} - {u.name}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td className="px-2 py-2">
                          <input
                            type="number"
                            step="0.0001"
                            value={line.unit_price || ""}
                            onChange={(e) =>
                              setLineField(index, "unit_price", e.target.value)
                            }
                            className="w-full border rounded-lg px-2 py-2 text-right"
                          />
                        </td>

                        <td className="px-2 py-2">
                          <input
                            type="number"
                            step="0.0001"
                            value={line.discount_percent || ""}
                            onChange={(e) =>
                              setLineField(index, "discount_percent", e.target.value)
                            }
                            className="w-full border rounded-lg px-2 py-2 text-right"
                          />
                        </td>

                        <td className="px-2 py-2 text-right">
                          {Number(line.net_unit_price || 0).toFixed(4)}
                        </td>

                        <td className="px-2 py-2 text-right">
                          {Number(line.line_total || 0).toFixed(4)}
                        </td>

                        <td className="px-2 py-2">
                          {!isLocked ? (
  <button
    type="button"
    onClick={() => removeLine(index)}
    className="px-3 py-1.5 rounded-lg border text-red-600"
  >
    Remove
  </button>
) : (
  <span className="text-slate-400 text-sm">Locked</span>
)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex justify-end">
              <div className="text-right">
                <div className="text-sm text-slate-500">Total Amount</div>
                <div className="text-2xl font-bold">
                  {Number(totalAmount || 0).toFixed(4)} {header.currency || "RSD"}
                </div>
              </div>
            </div>
          </div>

          {!isNew && ["APPROVED", "ORDERED", "PARTIAL_RECEIVED"].includes(header.status) ? (
            <div className="bg-white rounded-xl shadow-sm border p-4 space-y-4">
              <div>
                <h3 className="text-lg font-semibold">Receive Goods</h3>
                <p className="text-slate-600 text-sm">
                  Receive ordered items into inventory.
                </p>
              </div>

              <div className="grid gap-3 md:grid-cols-4">
                <select
                  value={receiptWarehouseId}
                  onChange={(e) => setReceiptWarehouseId(e.target.value)}
                  className="border rounded-lg px-3 py-2"
                >
                  <option value="">Select warehouse</option>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.code ? `${w.code} - ` : ""}
                      {w.name}
                    </option>
                  ))}
                </select>

                <select
                  value={receiptLocationId}
                  onChange={(e) => setReceiptLocationId(e.target.value)}
                  className="border rounded-lg px-3 py-2"
                >
                  <option value="">Select location</option>
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.code ? `${l.code} - ` : ""}
                      {l.name}
                    </option>
                  ))}
                </select>

                <input
                  type="date"
                  value={receiptPostingDate}
                  onChange={(e) => setReceiptPostingDate(e.target.value)}
                  className="border rounded-lg px-3 py-2"
                />

                <input
                  type="text"
                  value={receiptRemarks}
                  onChange={(e) => setReceiptRemarks(e.target.value)}
                  placeholder="Receipt remarks"
                  className="border rounded-lg px-3 py-2"
                />
              </div>

              <div className="overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Line</th>
                      <th className="px-3 py-2 text-left">Item</th>
                      <th className="px-3 py-2 text-right">Ordered</th>
                      <th className="px-3 py-2 text-right">Already Received</th>
                      <th className="px-3 py-2 text-right">Remaining</th>
                      <th className="px-3 py-2 text-right">Receive Now</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line) => (
                      <tr key={line.id || line.line_no} className="border-t">
                        <td className="px-3 py-2">{line.line_no}</td>
                        <td className="px-3 py-2">
                          {line.item_sku ? `${line.item_sku} - ` : ""}
                          {line.item_name || line.description || line.item_id}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {Number(line.qty || 0).toFixed(4)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {Number(line.received_qty || 0).toFixed(4)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {Number(getRemainingQty(line) || 0).toFixed(4)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number"
                            step="0.0001"
                            min="0"
                            max={getRemainingQty(line)}
                            value={receiptQtyByLine[line.id] ?? ""}
                            onChange={(e) =>
                              setReceiptQtyByLine((prev) => ({
                                ...prev,
                                [line.id]: e.target.value,
                              }))
                            }
                            className="w-32 border rounded-lg px-2 py-2 text-right"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleReceive}
                  className="px-4 py-2 rounded-lg bg-black text-white"
                >
                  Receive Goods
                </button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}